import { splunk } from '@fs/zion-splunk-logger'
import cuss from './cussDetector/index'
import randomStringDetector from './randomStringDetector'
import getCollectionIds from './getCollectionIds'

// Format tasks into strings for the log
const formatTasksIntoStrings = (tasks, type) => {
  return tasks.map((task) => {
    let taskStr = `ark: ${task.ark}, editedValue: ${task.editedValue}, originalValue: ${task.originalValue}`
    if (type === 'garbage') taskStr += `, spamConfidenceRating: ${task.spamConfidenceRating}`
    return taskStr
  })
}

// Splunk logging for garbage spam (user enters gibberish into the edit field)
export const logGarbageSpam = async (cisId, collectionIds, consecutiveEditSpamEpisodes, garbageTasks) => {
  const [task1, task2, task3] = formatTasksIntoStrings(garbageTasks, 'garbage')

  const logObject = {
    cisId,
    collectionIds,
    consecutiveEditSpamEpisodes,
    message: `garbage edit spamming on quick name review`,
    task1,
    task2,
    task3,
  }
  splunk.log(logObject)
}

// Splunk logging for cuss spam
export const logCussSpam = (cisId, collectionIds, consecutiveEditSpamEpisodes, cussTasks) => {
  const [task1, task2, task3] = formatTasksIntoStrings(cussTasks)

  const logObject = {
    cisId,
    collectionIds,
    consecutiveEditSpamEpisodes,
    message: `cuss edit spamming on quick name review`,
    task1,
    task2,
    task3,
  }
  splunk.log(logObject)
}

// Get the most recent tasks that are garbage
const getGarbageTasks = (taskCache, garbageThreshold) => {
  if (!taskCache.current) return []

  return (
    taskCache.current
      .map((task) => {
        const spamConfidenceRating = randomStringDetector(task.editedValue)

        // If the spam confidence rating is high, return the task info we need for the log
        return spamConfidenceRating >= garbageThreshold
          ? {
              ark: task.ark,
              editedValue: task.editedValue,
              originalValue: task.originalValue,
              spamConfidenceRating,
            }
          : // Otherwise, return false so we know the task isn't spam
            false
      })
      // Don't include tasks that aren't spam
      .filter(Boolean)
      // Don't include tasks where the editedValue and originalValue are the same
      .filter((task) => task.editedValue.trim() !== task.originalValue.trim())
  )
}

// Get the most recent tasks that are cuss words
const getCussTasks = (taskCache) => {
  if (!taskCache.current) return []

  return [
    // Check to see if the editedValue is a cuss word
    ...(taskCache.current
      ?.filter((task) => {
        return cuss[task.editedValue.toLowerCase()]
      })
      // Return the task info we need for the log
      .map((task) => {
        return {
          ark: task.ark,
          editedValue: task.editedValue,
          originalValue: task.originalValue,
        }
      }) || []),
  ]
}

export default async function checkForEditSpam({
  cisId,
  consecutiveSpamEpisodes,
  taskCache,
  editThreshold,
  garbageThreshold,
  incrementConsecutiveSpam,
}) {
  const garbageTasks = getGarbageTasks(taskCache, garbageThreshold)
  const cussTasks = getCussTasks(taskCache)

  // If all the most recent edits are garbage, log it and clear the cache
  if (garbageTasks.length === editThreshold) {
    incrementConsecutiveSpam('edit')

    getCollectionIds(garbageTasks)
      .then((collectionIds) => {
        logGarbageSpam(cisId, collectionIds, consecutiveSpamEpisodes?.current.count, garbageTasks)
      })
      .catch((error) => console.error('Error logging garbage spam:', error))

    taskCache.current = []
    // If all the most recent edits are cuss words, log it and clear the cache
  } else if (cussTasks.length === editThreshold) {
    incrementConsecutiveSpam('edit')

    getCollectionIds(cussTasks)
      .then((collectionIds) => {
        logCussSpam(cisId, collectionIds, consecutiveSpamEpisodes?.current.count, cussTasks)
      })
      .catch((error) => console.error('Error logging cuss spam:', error))

    taskCache.current = []
  } else {
    // Otherwise, we reset the consecutive spam and only keep the most recent edits
    consecutiveSpamEpisodes.current = { type: 'edit', count: 0 }
    taskCache.current = taskCache.current?.slice(-editThreshold + 1)
  }
}
